import { Button } from '@reflex/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { ShareInviteList } from './ShareInviteList';
import { shareDialogCopy, type InviteShare } from './inviteShare';

interface ShareInviteDialogProps {
  /** The links to show. `null` closes the dialog. */
  shares: InviteShare[] | null;
  /**
   * Called when the dialog is dismissed, for the caller to drop the links it
   * is holding. A plain close callback rather than Radix's `onOpenChange`:
   * every caller only ever cares about the closing edge, and inverting the
   * boolean at each of them was a chance to get it backwards.
   */
  onClose: () => void;
}

/**
 * Standalone dialog for links minted by something that is not already a
 * dialog — a resend from the pending list, or the admin panel's send form.
 *
 * A surface that is itself a dialog must render {@link ShareInviteList} in
 * its own body instead of opening this one: closing one Radix dialog while
 * opening another in the same commit races, and the layer on its way out can
 * dismiss the one arriving.
 */
export function ShareInviteDialog({ shares, onClose }: ShareInviteDialogProps) {
  const copy = shares && shares.length > 0 ? shareDialogCopy(shares) : null;
  return (
    <Dialog
      open={!!shares}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent data-testid="share-invite-dialog">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? 'Share invite link'}</DialogTitle>
          <DialogDescription>{copy?.description ?? null}</DialogDescription>
        </DialogHeader>
        {shares && shares.length > 0 && <ShareInviteList shares={shares} />}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
